import { inject, Injectable } from '@angular/core';
import { AppConstants } from '@app/app.constants';
import { EMPTY_SEARCH_RESULTS } from '@components/global-search/global-search.constants';
import {
  SearchResult,
  SearchResultsGroup,
  SearchSuggestion
} from '@interfaces/search-result.interface';
import { Article } from '@models/article';
import { CategoryGroup } from '@models/category-group';
import { Folder } from '@models/folder';
import { Page } from '@models/page';
import { ArticlesService } from '@services/articles/articles.service';
import { CategoriesGroupsService } from '@services/categories-groups/categories-groups.service';
import { FoldersService } from '@services/folders/folders.service';
import { PagesService } from '@services/pages/pages.service';
import { StorageService } from '@services/storage/storage.service';
import { formatDateDeAt } from '@shared/utils/format-date.util';
import {
  catchError,
  debounceTime,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap
} from 'rxjs';

export { EMPTY_SEARCH_RESULTS };

const MAX_RECENT_SEARCHES = 4;

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private readonly articlesService = inject(ArticlesService);
  private readonly pagesService = inject(PagesService);
  private readonly foldersService = inject(FoldersService);
  private readonly categoriesGroupsService = inject(CategoriesGroupsService);
  private readonly storageService = inject(StorageService);
  private readonly categoryGroups$ = this.categoriesGroupsService.getItems<CategoryGroup>().pipe(
    tap(groups => {
      this.pageCategoryGroupMap = this.buildPageCategoryGroupMap(groups);
    }),
    shareReplay(1)
  );
  public readonly suggestions$ = this.categoryGroups$.pipe(map(groups => this.getSuggestions(groups)));
  private pageCategoryGroupMap: Map<string, string> | null = null;

  public search(query$: Observable<string>): Observable<SearchResultsGroup> {
    return query$.pipe(
      debounceTime(250),
      switchMap(query => {
        const trimmed = query.trim();
        if (!trimmed) {
          return of(EMPTY_SEARCH_RESULTS);
        }
        return this.categoryGroups$.pipe(
          switchMap(() =>
            forkJoin({
              news: this.articlesService.search<Article>(trimmed).pipe(catchError(() => of([]))),
              products: this.pagesService.search<Page>(trimmed).pipe(catchError(() => of([]))),
              folders: this.foldersService.search<Folder>(trimmed).pipe(catchError(() => of([])))
            })
          ),
          map(({ news, products, folders }) => ({
            news: news.map(article => this.mapArticle(article)),
            products: products
              .map(page => this.mapPage(page))
              .filter((result): result is SearchResult => result !== null),
            folders: folders.map(folder => this.mapFolder(folder))
          }))
        );
      })
    );
  }

  public async getRecentSearches(): Promise<string[]> {
    const stored = await this.storageService.get(AppConstants.RECENT_SEARCHES);
    return Array.isArray(stored) ? (stored as string[]) : [];
  }

  public async saveRecentSearch(query: string): Promise<void> {
    const trimmed = query.trim();
    if (!trimmed) {
      return;
    }
    const recent = await this.getRecentSearches();
    const updated = [trimmed, ...recent.filter(term => term !== trimmed)].slice(0, MAX_RECENT_SEARCHES);
    await this.storageService.set(AppConstants.RECENT_SEARCHES, updated);
  }

  public getSuggestions(categoryGroups: CategoryGroup[]): SearchSuggestion[] {
    const suggestions: SearchSuggestion[] = [
      {
        icon: 'news-black.svg',
        label: 'Alle News',
        subtitle: 'Aktuelle Meldungen und Updates',
        routerLink: ['/news']
      }
    ];

    for (const group of categoryGroups) {
      if (group.pages_1.length || group.pages_2.length) {
        const groupId = group.slug ?? group.id;
        suggestions.push({
          icon: 'category.svg',
          label: group.title,
          subtitle: 'Produkte anzeigen',
          routerLink: ['/pages', groupId]
        });
      }
    }

    suggestions.push({
      icon: 'briefcase.svg',
      label: 'Beratungsmappen',
      subtitle: 'Alle Beratungsmappen durchsuchen',
      routerLink: ['/folders']
    });

    return suggestions;
  }

  private buildPageCategoryGroupMap(groups: CategoryGroup[]): Map<string, string> {
    const map = new Map<string, string>();
    for (const group of groups) {
      const groupId = group.slug ?? group.id;
      for (const rel of [...group.pages_1, ...group.pages_2]) {
        if (rel.pages_id?.id) {
          map.set(rel.pages_id.id, groupId);
        }
      }
    }
    return map;
  }

  private mapArticle(article: Article): SearchResult {
    const date = article.date_updated ?? article.date_created;
    return {
      id: article.id,
      type: 'news',
      title: article.title,
      meta: formatDateDeAt(date),
      thumbUrl: article.image,
      routerLink: ['/news', article.slug ?? article.id]
    };
  }

  private mapPage(page: Page): SearchResult | null {
    const categoryGroupId = this.pageCategoryGroupMap?.get(page.id);
    if (!categoryGroupId) {
      return null;
    }
    return {
      id: page.id,
      type: 'product',
      title: page.title,
      meta: page.category?.title ?? '',
      routerLink: ['/pages', categoryGroupId, page.slug ?? page.id]
    };
  }

  private mapFolder(folder: Folder): SearchResult {
    const docCount = folder.links?.length ?? 0;
    return {
      id: folder.id,
      type: 'folder',
      title: folder.title ?? folder.category?.title ?? '',
      meta: `${docCount} Dokumente`,
      routerLink: ['/folders', folder.slug ?? folder.id]
    };
  }
}
